import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ApplicationContext } from '../../context/GlobalState';
import { t, extractLocaleFromPath, localizedPath } from '../../i18n';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import { buildBlogIndexSchema } from '../../utils/schemaMarkup';
import gaHero from '../../assets/images/heroes/ga-hero-main.webp';
import ncHero from '../../assets/images/heroes/nc-hero-main.webp';
import './BlogIndex.css';

const BRAND_HEROES = { gso: gaHero, 'apex-nc': ncHero, 'apex-tn': ncHero };
const PAGE_SIZE = 12;
const CATEGORIES = ['treatment', 'conditions', 'prevention', 'news', 'legal', 'general'];

// Brand filtering: gso → gaspineortho.com, apex-nc → apexosn.com, apex-tn → show all
function filterArticlesByBrand(articles, brandKey) {
  if (brandKey === 'gso') return articles.filter(a => a.source && a.source.includes('gaspineortho'));
  if (brandKey === 'apex-nc') return articles.filter(a => a.source && a.source.includes('apexosn.com'));
  if (brandKey === 'apex-tn') return articles.filter(a => a.source && a.source.includes('apexosntn'));
  // unknown: show all
  return articles;
}

function formatDate(dateStr, locale = 'en') {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const localeMap = { en: 'en-US', es: 'es-ES', pt: 'pt-BR', ko: 'ko-KR', ja: 'ja-JP', fr: 'fr-FR' };
  return d.toLocaleDateString(localeMap[locale] || 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * Returns localized { title, excerpt, body } for an article.
 * Falls back to English if locale is 'en' or translation missing.
 */
export function getLocalizedArticle(article, locale) {
  const body = article.content || article.body || '';
  if (locale === 'en' || !article.translations || !article.translations[locale]) {
    return {
      title: article.title || '',
      excerpt: article.excerpt || '',
      body,
    };
  }
  const tr = article.translations[locale];
  return {
    title: tr.title || article.title || '',
    excerpt: tr.excerpt || article.excerpt || '',
    body: tr.content || tr.body || body,
  };
}

// ---------------------------------------------------------------------------
// BlogCard — uses React state for image error (no DOM traversal)
// ---------------------------------------------------------------------------
export function BlogCard({ article, locale }) {
  const [imgError, setImgError] = useState(false);
  const imgUrl = article.images?.[0];
  const localized = getLocalizedArticle(article, locale);

  return (
    <Link
      to={localizedPath(`/blog/${article.slug}`, locale)}
      className="blog-card glass-card"
    >
      <div
        className="blog-card-img"
        style={{
          height: 185,
          borderRadius: '12px 12px 0 0',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, var(--color-primary, #376E78) 0%, var(--color-accent, #0d9488) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {imgUrl && !imgError ? (
          <img
            src={imgUrl}
            alt={localized.title}
            onError={() => setImgError(true)}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <span
            className="blog-img-fallback"
            style={{ fontSize: '2.5rem', opacity: 0.4, display: 'flex', position: 'absolute' }}
          >🦴</span>
        )}
      </div>
      <div className="blog-card-body">
        {article.tags && article.tags.length > 0 && (
          <span className="glass-badge">{article.tags[0]}</span>
        )}
        <h2>{localized.title}</h2>
        <p className="blog-card-date">{formatDate(article.date, locale)}</p>
        <p className="blog-card-excerpt">{localized.excerpt}</p>
        <span className="blog-read-more">{t("blog.readMore", locale)} →</span>
      </div>
    </Link>
  );
}

export default function BlogIndex() {
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand;
  const brandKey = brand.brandKey || 'gso';
  const heroImg = BRAND_HEROES[brandKey] || gaHero;
  const baseDomain = brand?.domain || 'gaspineortho.com';

  const [page, setPage] = useState(1);
  const [allArticles, setAllArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/data/articles-scraped.json')
      .then(res => {
        if (!res.ok) throw new Error(`Failed to load articles: ${res.status}`);
        return res.json();
      })
      .then(data => {
        setAllArticles(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load articles:', err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const filtered = filterArticlesByBrand(allArticles, brandKey);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const blogSchema = buildBlogIndexSchema({ brand, articles: filtered });

  // Category counts
  const categoryCounts = {};
  for (const a of filtered) {
    const cat = a.category || 'general';
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  }

  const handleCategoryClick = (catSlug) => {
    navigate(localizedPath(`/blog/category/${catSlug}`, locale));
  };

  return (
    <div className="blog-index">
      <Helmet>
        <title>{brand.name} Blog | Health Articles & Treatment Guides</title>
        <meta name="description" content={`Health articles, treatment guides, and news from ${brand.name}. Expert orthopedic and injury care information.`} />
        <meta property="og:title" content={`${brand.name} Blog`} />
        <meta property="og:description" content={`Health articles and treatment guides from ${brand.name}.`} />
        <meta property="og:url" content={`https://${baseDomain}/blog`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://${baseDomain}/assets/brand/${brand.brandKey}/logo-color.webp`} />
        <link rel="canonical" href={`https://${baseDomain}/blog`} />
      </Helmet>
      <HreflangTags baseDomain={baseDomain} pathWithoutLocale="/blog" />
      {blogSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(blogSchema) }} />}

      <section
        className="blog-hero"
        style={{
          backgroundImage: `linear-gradient(var(--glass-hero-overlay, rgba(26,54,93,0.72)), var(--glass-hero-overlay, rgba(26,54,93,0.72))), url(${heroImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container">
          <h1>{t("blog.title", locale)}</h1>
          <p>{t('blog.subtitle', locale).replace('{{name}}', brand.name)}</p>
          {!loading && <p style={{ opacity: 0.75, fontSize: '0.95rem' }}>{filtered.length} articles</p>}
        </div>
      </section>

      <section className="blog-grid-section section-padding">
        <div className="container">
          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--color-text-muted)' }}>
              <p>{t("common.loading", locale)}</p>
            </div>
          )}
          {error && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--color-error, #e53e3e)' }}>
              <p>{t('blog.loadError', locale)}</p>
            </div>
          )}
          {!loading && !error && (
            <>
              {/* Category filter bar */}
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  overflowX: 'auto',
                  paddingBottom: 8,
                  marginBottom: 28,
                  flexWrap: 'wrap',
                }}
              >
                <button
                  className="glass-badge"
                  onClick={() => { setPage(1); window.scrollTo(0, 0); }}
                  style={{
                    cursor: 'pointer',
                    border: '2px solid var(--color-primary)',
                    background: 'var(--color-primary)',
                    color: '#fff',
                    fontWeight: 700,
                    padding: '6px 14px',
                    borderRadius: 20,
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                    textDecoration: 'none',
                  }}
                >
                  {t('blog.category.all', locale)} ({filtered.length})
                </button>
                {CATEGORIES.filter(cat => categoryCounts[cat] > 0).map(cat => (
                  <button
                    key={cat}
                    className="glass-badge"
                    onClick={() => handleCategoryClick(cat)}
                    style={{
                      cursor: 'pointer',
                      border: '1px solid var(--color-border)',
                      background: 'transparent',
                      color: 'var(--color-text)',
                      padding: '6px 14px',
                      borderRadius: 20,
                      fontSize: '0.85rem',
                      whiteSpace: 'nowrap',
                      textDecoration: 'none',
                    }}
                  >
                    {t(`blog.category.${cat}`, locale)} ({categoryCounts[cat] || 0})
                  </button>
                ))}
              </div>

              <div className="blog-card-grid">
                {visible.map((article) => (
                  <BlogCard key={article.slug} article={article} locale={locale} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 40, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo(0, 0); }}
                    disabled={page === 1}
                    className="glass-btn glass-btn-outline"
                    style={{ padding: '8px 16px', fontSize: '0.9rem' }}
                  >
                    {t('blog.prev', locale)}
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <button
                      key={n}
                      onClick={() => { setPage(n); window.scrollTo(0, 0); }}
                      className={`glass-btn ${n === page ? 'glass-btn-primary' : 'glass-btn-outline'}`}
                      style={{ padding: '8px 14px', fontSize: '0.9rem', minWidth: 40 }}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo(0, 0); }}
                    disabled={page === totalPages}
                    className="glass-btn glass-btn-outline"
                    style={{ padding: '8px 16px', fontSize: '0.9rem' }}
                  >
                    {t('blog.next', locale)}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
